import { useState } from 'react';
import { RefreshCw, Activity, CheckCircle, XCircle, Clock, Gauge, Server } from 'lucide-react';
import { Button, Card, CardContent, Skeleton } from '@makanly/ui-kit';
import { useStatusMonitor } from './hooks/useStatusMonitor';
import { useNotifications } from './hooks/useNotifications';
import { useDarkMode } from './hooks/useDarkMode';
import { ServiceGroup } from './components/ServiceGroup';
import { ConnectionStatus } from './components/ConnectionStatus';
import { NotificationToggle } from './components/NotificationToggle';
import { PerfDashboard } from './components/PerfDashboard';
import { groupByCategory, calculateHealthSummary } from './types';

type TabType = 'status' | 'performance';

function App() {
  const [activeTab, setActiveTab] = useState<TabType>('status');

  // Enable automatic dark mode based on system preference
  useDarkMode();

  const { settings, toggleEnabled, toggleSound, requestPermission, handleAlert } =
    useNotifications();

  const { services, connection, refresh, lastUpdate } = useStatusMonitor({
    onAlert: handleAlert,
  });

  const groupedServices = groupByCategory(services);
  const summary = calculateHealthSummary(services);
  const isLoading = services.length === 0 && connection.isConnected;

  const lastUpdateFormatted = lastUpdate ? new Date(lastUpdate).toLocaleString() : 'Never';

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-card/80 dark:bg-card/90 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <Activity className="w-8 h-8 text-text-primary" />
              <div>
                <h1 className="text-xl font-bold text-text-primary">System Status</h1>
                <p className="text-sm text-text-secondary">Makanly Platform Health Monitor</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <ConnectionStatus connection={connection} />
              <NotificationToggle
                settings={settings}
                onToggleEnabled={toggleEnabled}
                onToggleSound={toggleSound}
                onRequestPermission={requestPermission}
              />
              {activeTab === 'status' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={refresh}
                  disabled={!connection.isConnected}
                  className="px-3 py-1.5 h-auto gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span className="hidden sm:inline">Refresh</span>
                </Button>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 mt-4 -mb-4 border-b-0">
            <button
              onClick={() => setActiveTab('status')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                activeTab === 'status'
                  ? 'bg-card text-text-primary border-x border-t border-border'
                  : 'text-text-secondary hover:text-text-primary hover:bg-accent'
              }`}
            >
              <Server className="w-4 h-4" />
              Service Status
            </button>
            <button
              onClick={() => setActiveTab('performance')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                activeTab === 'performance'
                  ? 'bg-card text-text-primary border-x border-t border-border'
                  : 'text-text-secondary hover:text-text-primary hover:bg-accent'
              }`}
            >
              <Gauge className="w-4 h-4" />
              Performance
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {activeTab === 'status' ? (
          <>
            {/* Health Summary Card */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
                  {/* Total Services */}
                  <div className="text-center">
                    <div className="text-3xl font-bold text-text-primary">
                      {isLoading ? <Skeleton className="h-9 w-12 mx-auto" /> : summary.total}
                    </div>
                    <div className="text-sm text-text-secondary mt-1">Total Services</div>
                  </div>

                  {/* Healthy */}
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-2">
                      <CheckCircle className="w-6 h-6 text-green-500 dark:text-green-400" />
                      <span className="text-3xl font-bold text-green-600 dark:text-green-400">
                        {isLoading ? <Skeleton className="h-9 w-8" /> : summary.healthy}
                      </span>
                    </div>
                    <div className="text-sm text-text-secondary mt-1">Healthy</div>
                  </div>

                  {/* Unhealthy */}
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-2">
                      <XCircle className="w-6 h-6 text-red-500 dark:text-red-400" />
                      <span className="text-3xl font-bold text-red-600 dark:text-red-400">
                        {isLoading ? <Skeleton className="h-9 w-8" /> : summary.unhealthy}
                      </span>
                    </div>
                    <div className="text-sm text-text-secondary mt-1">Unhealthy</div>
                  </div>

                  {/* Uptime Percentage */}
                  <div className="text-center">
                    <div className="text-3xl font-bold text-text-primary">
                      {isLoading ? (
                        <Skeleton className="h-9 w-16 mx-auto" />
                      ) : (
                        `${summary.percentage}%`
                      )}
                    </div>
                    <div className="text-sm text-text-secondary mt-1">Healthy Rate</div>
                  </div>
                </div>

                {/* Last Update */}
                <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-border">
                  <Clock className="w-4 h-4 text-text-secondary" />
                  <span className="text-sm text-text-secondary">
                    Last updated: {lastUpdateFormatted}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Service Groups */}
            {isLoading ? (
              <div className="space-y-6">
                {['Backend', 'Frontend', 'Infrastructure'].map((category) => (
                  <div key={category}>
                    <Skeleton className="h-12 w-full rounded-lg mb-4" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {[1, 2, 3].map((i) => (
                        <Skeleton key={i} className="h-24 rounded-card" />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <>
                {/* Backend Services */}
                {groupedServices.backend.length > 0 && (
                  <ServiceGroup category="backend" services={groupedServices.backend} />
                )}

                {/* Frontend Apps */}
                {groupedServices.frontend.length > 0 && (
                  <ServiceGroup category="frontend" services={groupedServices.frontend} />
                )}

                {/* Infrastructure */}
                {groupedServices.infrastructure.length > 0 && (
                  <ServiceGroup
                    category="infrastructure"
                    services={groupedServices.infrastructure}
                  />
                )}
              </>
            )}

            {/* Empty State */}
            {!isLoading && services.length === 0 && !connection.isConnected && (
              <Card>
                <CardContent className="p-12 text-center">
                  <Activity className="w-12 h-12 text-text-secondary mx-auto mb-4" />
                  <h2 className="text-lg font-semibold text-text-primary mb-2">
                    Unable to Connect
                  </h2>
                  <p className="text-text-secondary mb-4">
                    Cannot connect to the monitor server. Make sure it's running on port 7101.
                  </p>
                  <code className="block bg-secondary p-3 rounded-lg text-sm font-mono">
                    pnpm --filter @makanly/system-status dev:server
                  </code>
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          /* Performance Dashboard */
          <PerfDashboard />
        )}
      </main>

      {/* Footer */}
      <footer className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-center">
        <p className="text-sm text-text-secondary">
          System Status Monitor •{' '}
          {activeTab === 'status'
            ? 'Refresh interval: 5 minutes'
            : 'Performance interval: 10 seconds'}
        </p>
      </footer>
    </div>
  );
}

export default App;
